import type { HomeDestination } from './home-destination';

export type NeedsAttentionKey =
  | 'failedCredentials'
  | 'lastScanFailed'
  | 'discoveryFailed'
  | 'waitingForInput'
  | 'criticalFindings'
  | 'blockedExecutions'
  | 'pausedExecutions'
  | 'blockedSignals'
  | 'signalsNeedingReview'
  | 'unpublishedFindings'
  | 'failedExecutions';

export type NeedsAttentionCounts = Record<NeedsAttentionKey, number>;

export type NeedsAttentionItem = {
  key: NeedsAttentionKey;
  count: number;
  target: HomeDestination;
};

type StaticNeedsAttentionKey = Exclude<NeedsAttentionKey, 'waitingForInput'>;

const NEEDS_ATTENTION_TARGETS = {
  failedCredentials: { kind: 'settings', section: 'credentials' },
  lastScanFailed: { kind: 'settings', section: 'general' },
  discoveryFailed: { kind: 'settings', section: 'general' },
  criticalFindings: {
    kind: 'list',
    destination: {
      list: 'findings',
      filters: { severityFilter: ['Critical'] },
    },
  },
  blockedExecutions: {
    kind: 'list',
    destination: { list: 'executions', filters: { statusFilter: ['BLOCKED'] } },
  },
  pausedExecutions: {
    kind: 'list',
    destination: { list: 'executions', filters: { statusFilter: ['PAUSED'] } },
  },
  blockedSignals: {
    kind: 'list',
    destination: { list: 'signals', filters: { statusFilter: ['BLOCKED'] } },
  },
  signalsNeedingReview: {
    kind: 'list',
    destination: {
      list: 'signals',
      filters: { stageFilter: ['NEEDS_REVIEW'] },
    },
  },
  unpublishedFindings: {
    kind: 'list',
    destination: {
      list: 'findings',
      filters: { publishedFilter: 'unpublished', severityFilter: [] },
    },
  },
  failedExecutions: {
    kind: 'list',
    destination: { list: 'executions', filters: { statusFilter: ['FAILED'] } },
  },
} as const satisfies Record<StaticNeedsAttentionKey, HomeDestination>;

export const NEEDS_ATTENTION_PRIORITY: readonly NeedsAttentionKey[] = [
  'failedCredentials',
  'lastScanFailed',
  'discoveryFailed',
  'waitingForInput',
  'criticalFindings',
  'blockedExecutions',
  'pausedExecutions',
  'blockedSignals',
  'signalsNeedingReview',
  'unpublishedFindings',
  'failedExecutions',
];

const resolveTarget = ({
  key,
  waitingForInputThreadId,
}: {
  key: NeedsAttentionKey;
  waitingForInputThreadId: string | null;
}): HomeDestination =>
  key === 'waitingForInput'
    ? { kind: 'copilot', threadId: waitingForInputThreadId }
    : NEEDS_ATTENTION_TARGETS[key];

export const buildNeedsAttentionItems = ({
  counts,
  waitingForInputThreadId = null,
}: {
  counts: Partial<NeedsAttentionCounts>;
  waitingForInputThreadId?: string | null;
}): NeedsAttentionItem[] =>
  NEEDS_ATTENTION_PRIORITY.flatMap((key) => {
    const count = counts[key] ?? 0;

    return count > 0
      ? [
          {
            key,
            count,
            target: resolveTarget({ key, waitingForInputThreadId }),
          },
        ]
      : [];
  });
